import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import bankInfoService from "../../../services/bankInfoService";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    Container,
    Card,
    Box,
    Typography,
    CardContent,
    Button,
    Breadcrumbs,
    Avatar,
    IconButton,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import { instanceToken } from "../../../utils/constant";
import LoadingOverlay from "../../../components/LoadingOverlay";

const styleR = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 350,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

const BankInfos = ({ homeAlert }) => {
    const [bankInfos, setBankInfos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [openR, setOpenR] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);

    useEffect(() => {
        fetchBankInfos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBankInfos = async () => {
        try {
            const res = await bankInfoService.getAllBankInfos(instanceToken.token);
            const rows = Array.isArray(res.data) ? res.data : [];
            // Backend orders by sort_order ASC; keep stable client order
            setBankInfos(rows);
            setLoading(false);
        } catch (error) {
            console.error(error);
            homeAlert("Error fetching bank info!", true);
            setLoading(false);
        }
    };

    const persistOrder = async (nextRows) => {
        setReordering(true);
        const previous = bankInfos;
        setBankInfos(nextRows);
        try {
            await bankInfoService.reorderBankInfos(
                instanceToken.token,
                nextRows.map((row) => row.id)
            );
            homeAlert("Bank info order updated!", false);
        } catch (error) {
            console.error(error);
            setBankInfos(previous);
            const isNetworkError = error?.message === "Network Error";
            homeAlert(
                isNetworkError
                    ? "Failed to save order (network or CORS). Check API access and try again."
                    : error?.response?.data?.error || "Failed to update bank info order!",
                true
            );
        } finally {
            setReordering(false);
        }
    };

    const moveBank = async (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= bankInfos.length || reordering) {
            return;
        }
        const next = [...bankInfos];
        const [item] = next.splice(index, 1);
        next.splice(newIndex, 0, item);
        await persistOrder(next);
    };

    const handleDragStart = (event, index) => {
        if (reordering) {
            event.preventDefault();
            return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
        setDragIndex(index);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDragEnd = () => {
        setDragIndex(null);
    };

    const handleDrop = async (event, targetIndex) => {
        event.preventDefault();
        if (reordering) {
            return;
        }
        const fromIndex = Number(event.dataTransfer.getData("text/plain"));
        if (Number.isNaN(fromIndex) || fromIndex === targetIndex) {
            setDragIndex(null);
            return;
        }
        const next = [...bankInfos];
        const [item] = next.splice(fromIndex, 1);
        next.splice(targetIndex, 0, item);
        setDragIndex(null);
        await persistOrder(next);
    };

    const handleOpenDelete = (id) => {
        setSelectedId(id);
        setOpenR(true);
    };

    const handleDelete = async () => {
        try {
            await bankInfoService.deleteBankInfo(instanceToken.token, selectedId);
            homeAlert("Bank Info deleted successfully!", false);
            setOpenR(false);
            fetchBankInfos();
        } catch (error) {
            console.error(error);
            homeAlert("Delete failed!", true);
        }
    };

    if (loading) {
        return <LoadingOverlay message="Loading bank information..." />;
    }

    return (
        <>
            <div role="presentation">
                <Breadcrumbs aria-label="breadcrumb">
                    <Typography color="text.primary">Bank Information</Typography>
                </Breadcrumbs>
            </div>
            <Container
                maxWidth={false}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 2,
                }}
            >
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        Drag rows or use arrows to change display order on the mobile app.
                    </Typography>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/createBankInfo"
                        sx={{ backgroundColor: "#1976d2" }}
                    >
                        Add New Bank Info
                    </Button>
                </Box>
                <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                    <Box
                        sx={{
                            background: "linear-gradient(90deg, #3EABE9 0%, #1e88e5 100%)",
                            p: 2,
                            color: "white",
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold">
                            Bank Accounts List
                        </Typography>
                    </Box>
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer component={Box}>
                            <Table sx={{ minWidth: 650 }} aria-label="bank info table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell align="center" sx={{ fontWeight: "700", width: 90 }}>
                                            Order
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "700" }}>Logo</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Name</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "700" }}>Type</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "700" }}>QR</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Account Name</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Account Type</TableCell>
                                        <TableCell sx={{ fontWeight: "700" }}>Account Numbers</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "700" }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bankInfos.map((bank, index) => (
                                        <TableRow
                                            key={bank.id}
                                            onDragOver={handleDragOver}
                                            onDrop={(event) => handleDrop(event, index)}
                                            sx={{
                                                "&:last-child td, &:last-child th": { border: 0 },
                                                "&:hover": { backgroundColor: "#f9f9f9" },
                                                opacity: dragIndex === index ? 0.6 : 1,
                                                backgroundColor:
                                                    dragIndex !== null && dragIndex !== index
                                                        ? "inherit"
                                                        : undefined,
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
                                                    <Box
                                                        draggable={!reordering}
                                                        onDragStart={(event) => handleDragStart(event, index)}
                                                        onDragEnd={handleDragEnd}
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            cursor: reordering ? "wait" : "grab",
                                                            touchAction: "none",
                                                        }}
                                                        title="Drag to reorder"
                                                    >
                                                        <DragIndicatorIcon fontSize="small" color="action" />
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        disabled={index === 0 || reordering}
                                                        onClick={() => moveBank(index, -1)}
                                                        title="Move up"
                                                    >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        disabled={index === bankInfos.length - 1 || reordering}
                                                        onClick={() => moveBank(index, 1)}
                                                        title="Move down"
                                                    >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Avatar
                                                    src={bank.image_url}
                                                    variant="rounded"
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: "white",
                                                        p: 0.5,
                                                        border: "1px solid #eee",
                                                        display: "inline-flex",
                                                        "& img": { objectFit: "contain" },
                                                    }}
                                                    imgProps={{ crossOrigin: "anonymous" }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: "500" }}>{bank.name}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={bank.entry_type === "mmqr" ? "MMQR" : "Bank"}
                                                    color={bank.entry_type === "mmqr" ? "success" : "primary"}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {bank.qr_image_url ? (
                                                    <Avatar
                                                        src={bank.qr_image_url}
                                                        variant="rounded"
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            bgcolor: "white",
                                                            p: 0.5,
                                                            border: "1px solid #eee",
                                                            display: "inline-flex",
                                                            "& img": { objectFit: "contain" },
                                                        }}
                                                        imgProps={{ crossOrigin: "anonymous" }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{bank.account_name || bank.accounts?.[0]?.account_name || "N/A"}</TableCell>
                                            <TableCell>{bank.account_type || "N/A"}</TableCell>
                                            <TableCell>
                                                {bank.accounts && bank.accounts.length > 0 ? bank.accounts.map((acc, idx) => (
                                                    <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                        <Typography variant="body2">{acc.account_number}</Typography>
                                                        {acc.type && (
                                                            <Typography variant="caption" sx={{ ml: 0.5, px: 0.5, py: 0.1, backgroundColor: "#e3f2fd", borderRadius: 1, color: "#1976d2" }}>
                                                                {acc.type}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )) : (
                                                    <Typography variant="caption" color="text.secondary">N/A</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        component={Link}
                                                        to={`/updateBankInfo/${bank.id}`}
                                                        title="Edit"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleOpenDelete(bank.id)}
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>

            <Modal open={openR} onClose={() => setOpenR(false)}>
                <Box sx={styleR}>
                    <Typography variant="h6" fontWeight="bold">
                        Delete Confirmation
                    </Typography>
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                        Are you sure you want to permanently delete this bank information?
                    </Typography>
                    <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button onClick={() => setOpenR(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                        >
                            Confirm Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default BankInfos;
